import { ReactElement, useMemo } from 'react';
import LockUp from '../assets/brand/lock-up.svg';
import { useForm } from '@tanstack/react-form';
import { Input, Button, LinearLoader, IconButton } from '@sc/shared-ui';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useMutation } from '@apollo/client/react';
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { GQL_MUTATION_ACCEPT_INVITATION } from '../utility/queries';
import { useAlerts } from '../providers/alerts';
import { useAuthenticated } from '../providers/authenticated';
import { track } from '../utility/analytics';

export const AcceptInvitation = (): ReactElement => {
  const router = useRouter();
  const { useApolloError } = useAlerts();
  const onError = useApolloError();
  const { setIsAuthenticated } = useAuthenticated();

  const params = useSearch({
    from: '/_unauthenticated/accept-invitation',
  });

  const decoded = useMemo(() => {
    try {
      if (!params.token || !params.email) return null;

      const token = atob(params.token);
      const email = atob(params.email);

      return { token, email };
    } catch {
      //
    }

    return null;
  }, [params.token, params.email]);

  const [acceptInvitation, { loading }] = useMutation(
    GQL_MUTATION_ACCEPT_INVITATION,
    {
      onCompleted: () => {
        track('Invitation Accepted');

        setIsAuthenticated(new Date().getTime().toString());

        void router.navigate({ to: '/' });
      },
      onError,
    }
  );

  const form = useForm({
    defaultValues: { name: '' },
    onSubmit: ({ value }) => {
      if (!decoded) return;

      void acceptInvitation({
        variables: {
          input: {
            email: decoded.email,
            token: decoded.token,
            name: value.name,
          },
        },
      });
    },
  });

  const navigate = useNavigate();

  if (!decoded) {
    return (
      <div className="h-full w-full px-5 py-10 flex flex-col items-center justify-center">
        <div className="w-full max-w-105 flex flex-col gap-10 items-center">
          <LockUp className="h-5" />
          <div className="w-full flex flex-col items-center text-center p-5 bg-background-primary border border-grey-t-800 rounded-xs">
            <h1 className="font-grotesque font-semibold text-h1 leading-11 mb-5">
              Invalid invitation
            </h1>
            <p className="text-text-low-priority mb-8">
              This invitation link is invalid or has expired.
            </p>
            <Button
              button={{ text: 'Go to login', size: 'large' }}
              link={{ to: '/login' }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full px-5 py-10 flex flex-col items-center justify-center">
      {loading && (
        <div className="absolute inset-x-0 top-0">
          <LinearLoader />
        </div>
      )}
      <div className="w-full max-w-105 flex flex-col gap-10 items-center">
        <div className={`flex items-center w-full`}>
          <IconButton
            Icon={ArrowLeftIcon}
            size="large"
            onClick={() =>
              void navigate({
                to: '/login',
                search: {},
              })
            }
          />
        </div>
        <LockUp className="h-5" />
        <div className="w-full flex flex-col items-center space-y-3 p-5 bg-background-primary border border-grey-t-800 rounded-xs">
          <h1 className="font-grotesque font-semibold text-h1 leading-11 text-center mb-4">
            Accept your invitation
          </h1>
          <form
            onSubmit={(e) => {
              e.stopPropagation();

              e.preventDefault();

              void form.handleSubmit();
            }}
            className="space-y-5"
          >
            <form.Field
              name="name"
              children={(field) => (
                <Input
                  value={field.state.value}
                  label="Name"
                  required
                  className="w-88"
                  Icon={UserIcon}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              )}
            />
            <Button
              button={{
                isSubmit: true,
                text: 'Accept invitation',
                IconEnd: ArrowRightIcon,
                size: 'large',
                disabled: loading,
              }}
            />
          </form>
        </div>
      </div>
    </div>
  );
};
